
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { AssessmentResult } from '@/types/assessment';
import CareerRecommendations from './CareerRecommendations';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ReportSummaryProps {
  report?: string;
  result?: AssessmentResult;
}

const ReportSummary = ({ report, result }: ReportSummaryProps) => {
  // Enhanced function to convert markdown to HTML with improved table support
  const formatMarkdown = (text: string) => {
    if (!text) return '';
    
    // Function to parse and convert markdown tables to HTML tables
    const parseTables = (content: string) => {
      // Find table sections in the markdown - tables have rows starting with |
      const tableRegex = /^\|(.+)\|$/gm;
      
      return content.replace(/(\|[^\n]+\|\n)((?:\|[^\n]+\|\n)+)/g, (match) => {
        const lines = match.split('\n').filter(line => line.trim().length > 0);
        
        if (lines.length < 2) return match; // Not a proper table
        
        // Extract header and rows
        const headerLine = lines[0];
        const separatorLine = lines[1];
        const dataLines = lines.slice(2);
        
        // Check if this is a valid table with separator line (contains -)
        if (!separatorLine.includes('-')) return match;
        
        // Parse header cells
        const headerCells = headerLine
          .split('|')
          .slice(1, -1)
          .map(cell => cell.trim());
        
        // Start building HTML table
        let tableHtml = '<div class="overflow-x-auto my-4">';
        tableHtml += '<table class="w-full border-collapse border border-gray-300">';
        
        // Add header row
        tableHtml += '<thead>';
        tableHtml += '<tr>';
        headerCells.forEach(cell => {
          tableHtml += `<th class="border border-gray-300 bg-brand-blue text-white px-3 py-2 text-left font-medium">${cell}</th>`;
        });
        tableHtml += '</tr>';
        tableHtml += '</thead>';
        
        // Add body rows
        tableHtml += '<tbody>';
        dataLines.forEach(line => {
          const cells = line
            .split('|')
            .slice(1, -1)
            .map(cell => cell.trim());
          
          tableHtml += '<tr>';
          cells.forEach(cell => {
            // Add special styling for "High" and "Very High" suitability
            let cellClass = 'border border-gray-300 px-3 py-2';
            
            if (cell.includes('**Very High**')) {
              cellClass += ' bg-green-100 font-semibold';
              cell = cell.replace(/\*\*/g, '');
            } else if (cell.includes('**High**')) {
              cellClass += ' bg-green-50 font-semibold';
              cell = cell.replace(/\*\*/g, '');
            } else if (cell.includes('*')) {
              // Handle asterisks as indicators in the table (not bold formatting)
              cell = cell.replace(/\*/g, '✓');
            }
            
            // Handle career items that should be highlighted
            if (cell.startsWith('**') && cell.endsWith('**')) {
              cellClass += ' text-brand-blue font-semibold';
              cell = cell.replace(/\*\*/g, '');
            }
            
            tableHtml += `<td class="${cellClass}">${cell}</td>`;
          });
          tableHtml += '</tr>';
        });
        tableHtml += '</tbody>';
        tableHtml += '</table>';
        tableHtml += '</div>';
        
        return tableHtml;
      });
    };
    
    // Process tables first to avoid conflicts with other formatting
    let formattedText = parseTables(text);
    
    // Convert markdown headings with better styling for the new format
    formattedText = formattedText
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold my-4 text-left">$1</h1>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold my-3 text-brand-blue text-left">$1</h2>')
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold my-2 text-left">$1</h3>')
      
      // Convert lists
      .replace(/^\d\. (.*$)/gim, '<li class="ml-5 list-decimal my-1 text-left">$1</li>')
      .replace(/^- (.*$)/gim, '<li class="ml-5 list-disc my-1 text-left">$1</li>')
      
      // Convert bold text - but not in tables as we already processed those
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\_\_(.*?)\_\_/g, '<strong>$1</strong>')
      
      // Convert italic text
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\_(.*?)\_/g, '<em>$1</em>')
      
      // Convert hyperlinks
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-brand-blue hover:underline" target="_blank">$1</a>')
      
      // Convert code blocks
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
      
      // Group list items
      .replace(/<li class="ml-5 list-decimal my-1 text-left">/g, '<ol class="list-decimal ml-5 my-2 text-left"><li class="text-left">')
      .replace(/<\/li>\s*<ol class="list-decimal ml-5 my-2 text-left">/g, '</li>')
      .replace(/<\/li>\s*<\/ol>\s*<\/li>/g, '</li></ol>')
      
      .replace(/<li class="ml-5 list-disc my-1 text-left">/g, '<ul class="list-disc ml-5 my-2 text-left"><li class="text-left">')
      .replace(/<\/li>\s*<ul class="list-disc ml-5 my-2 text-left">/g, '</li>')
      .replace(/<\/li>\s*<\/ul>\s*<\/li>/g, '</li></ul>')
      
      // Convert newlines to paragraphs
      .split('\n\n').join('</p><p class="my-2 text-left">')
      
      // Handle remaining newlines
      .replace(/\n/g, '<br />');
    
    return `<p class="my-2 text-left">${formattedText}</p>`;
  };

  // Improved detection for basic reports
  const isBasicReport = report && (
    report.includes("basic career guidance report") || 
    report.includes("add your OpenAI API key") ||
    report.includes("OpenAI API key not found") ||
    report.length < 200 // Basic reports are typically short
  );

  return (
    <>
      {/* Display career recommendations if available */}
      {result?.recommendedCareers && result.recommendedCareers.length > 0 && (
        <CareerRecommendations recommendations={result.recommendedCareers} />
      )}
      
      <Card className="shadow-lg mb-8">
        <CardContent className="p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-brand-blue mb-4 text-left">
            Career Guidance Summary
          </h2>
          <div className="prose max-w-none">
            <div className="bg-gray-50 border-l-4 border-brand-blue p-4 mb-6 rounded">
              <p className="italic text-gray-700 text-left">
                This is a summary of your career guidance report based on your assessment responses. 
                {isBasicReport && (
                  <span className="block mt-2 text-amber-600 font-semibold">
                    This is a basic report. For a more detailed AI-powered report, please add your OpenAI API key in the settings above.
                  </span>
                )}
              </p>
            </div>
            
            {report ? (
              <div 
                className="text-gray-700"
                dangerouslySetInnerHTML={{ __html: formatMarkdown(report) }}
              />
            ) : (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 text-brand-blue animate-spin mx-auto mb-4" />
                <p className="text-gray-500">
                  Your detailed report is being generated...
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default ReportSummary;
